import ApiService from "@/api";
import ProjectSearchRequestModel from "@/components/sidebar/models/ProjectSearchRequestModel";
import {
  mapGetTagsRequest,
  mapGetTagsResponse,
} from "@/components/task-forms/components/task-drawer/adapter/AdapterServiceMappers";
import { GetGlobalStatusesResponse, PageRequestModel } from "@/components/tasks-kanban/models";
import { ProjectModel, ResponseModel } from "@/models";
import {
  BaseProjectSearchResponse,
  GetDifficultyListDetailResponse,
  GetListTaskTypeRequest,
  GetPrioritiesListDetailResponse,
  GetStatusesListDetailResponse,
  GetTagsListDetailRequest,
  GetTagsListDetailResponse,
  NamespaceModel,
  ProjectQueueTaskListResponse,
} from "./models";
import { mapGetNamespacesRequest } from "@/pages/project-namespaces-page/adapter/AdapterServiceMappers";
import { mapGetNamespacesResponse } from "@/components/tasks-kanban/adapter/AdapterServiceMappers";
import ProjectNamespaceListRequest, {
  GetDifficultyListDetailRequest,
  GetPrioritiesListDetailRequest,
  GetStatusesListDetailRequest,
  ProjectNamespaceListResponse,
  ProjectQueueTaskListRequest,
} from "@/components/tasks-kanban/adapter/TaskKanbanAdapterService";
import {
  mapGetTypesRequest,
  mapGetTypesResponse,
} from "@/pages/namespace-types-settings-page/adapter/AdapterServiceMappers";
import { TypeModel } from "@/pages/namespace-types-settings-page/models";
import { OptionItemModel } from "../../../models";
import GetListTaskTypeResponse from "@/api/Main/dataSource/api/projects/task/i-service/models/task-type/response/GetListTaskTypeResponse";
import GetListStatusRequest from "./models/GetListStatusRequest";
import GetListStatusResponse from "@/api/Main/dataSource/api/projects/task/i-service/models/status/GetListStatusResponse";

/**Сервис для вызова запросов */
export class AdapterService {
  /**Сервис api */
  api = new ApiService();

  /**Получить список проектов */
  async GetProjects(
    request: ProjectSearchRequestModel,
  ): Promise<ResponseModel<BaseProjectSearchResponse>> {
    const result = new ResponseModel<BaseProjectSearchResponse>();
    try {
      const response = await this.api.Main.ProjectBaseService.searchAsync(request);
      result.Value = response;
    } catch (e) {
      result.IsSuccess = false;
      result.ErrorCode = e?.ErrorCode;
      result.ErrorMessage = e?.ErrorMessage;
      console.error("ERROR ProjectBaseService.Search", e);
    }
    return result;
  }

  /**Получить список следующих проектов */
  async GetNextListProjects(
    request: ProjectSearchRequestModel,
  ): Promise<ResponseModel<BaseProjectSearchResponse>> {
    const result = new ResponseModel<BaseProjectSearchResponse>();
    try {
      const response = await this.api.Main.ProjectBaseService.searchAsync(request);
      result.Value = response;
    } catch (e) {
      result.IsSuccess = false;
      result.ErrorCode = e?.ErrorCode;
      result.ErrorMessage = e?.ErrorMessage;
      console.error("ERROR ProjectBaseService.Search", e);
    }
    return result;
  }
  /**
   * * Получить список тегов
   * @param projectId Id проекта
   * @returns
   */
  getTags(request: GetTagsListDetailRequest) {
    return new Promise<ResponseModel<GetTagsListDetailResponse>>(async (resolve) => {
      const result = new ResponseModel<GetTagsListDetailResponse>();

      await this.api.Main.TagGetService.getTagsListDetailAsync(request)
        .then((resp) => {
          result.Value = resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error("ERROR getTags", e);
          return resolve(result);
        });
    });
  }
  /**
   * * Получить список неймспейсов
   * @param projectId ID проекта
   * @returns
   */
  getNamespaces(request: ProjectNamespaceListRequest) {
    return new Promise<ResponseModel<ProjectNamespaceListResponse>>(async (resolve) => {
      const result = new ResponseModel<ProjectNamespaceListResponse>();

      await this.api.Main.ProjectNamespaceService.getListAsync(request)
        .then((resp) => {
          result.Value = resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error("ERROR getNamespaces", e);
          return resolve(result);
        });
    });
  }

  /**
   * * Получить типы задач
   * @param namespaceId ID рабочей области
   * @returns
   */
  getTypes(req: GetListTaskTypeRequest) {
    return new Promise<ResponseModel<GetListTaskTypeResponse>>(async (resolve) => {
      const result = new ResponseModel<GetListTaskTypeResponse>();

      await this.api.Main.TaskTypeListService.getListTaskTypeAsync(req)
        .then((resp) => {
          result.Value = resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error("ERROR getTypes", e);
          return resolve(result);
        });
    });
  }
  /**
   * * Получение детального списка сложности задач
   */
  async getDifficultyListDetail(request: GetDifficultyListDetailRequest) {
    return new Promise<ResponseModel<GetDifficultyListDetailResponse>>(async (resolve) => {
      const result = new ResponseModel<GetDifficultyListDetailResponse>();
      await this.api.Main.TaskDifficultyListService.getDifficultyListDetailAsync(request)
        .then((resp) => {
          result.Value = resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error("ERROR getNamespaces", e);
          return resolve(result);
        });
    });
  }
  /**
   * Получение информации об очереди
   * @param queueId
   * @returns
   */
  async getQueue(
    request: ProjectQueueTaskListRequest,
  ): Promise<ResponseModel<ProjectQueueTaskListResponse>> {
    const result = new ResponseModel<ProjectQueueTaskListResponse>();
    try {
      const res = await this.api.Main.ProjectQueueTaskService.getListAsync(request);
      result.Value = res;
      result.IsSuccess = true;
      return result;
    } catch (e) {
      result.IsSuccess = false;
      result.ErrorCode = e?.ErrorCode;
      result.ErrorMessage = e?.ErrorMessage;
      console.error("ERROR getQueue", e);
      return result;
    }
  }
  /**
   * * Получение списка статусов с детальной информацией и фильтрами
   */
  async getStatusesListDetail(request: GetListStatusRequest) {
    return new Promise<ResponseModel<GetListStatusResponse>>(async (resolve) => {
      const result = new ResponseModel<GetListStatusResponse>();

      await this.api.Main.TaskStatusListService.getListAsync(request)
        .then((resp) => {
          result.Value = resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error("ERROR getNamespaces", e);
          return resolve(result);
        });
    });
  }
  /**
   * * Получение списка приоритетов с детальной информацией и фильтрами
   */
  async getPrioritiesListDetail(request: GetPrioritiesListDetailRequest) {
    return new Promise<ResponseModel<GetPrioritiesListDetailResponse>>(async (resolve) => {
      const result = new ResponseModel<GetPrioritiesListDetailResponse>();

      await this.api.Main.TaskPriorityGetService.getPrioritiesListDetailAsync(request)
        .then((resp) => {
          result.Value = resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error("ERROR getNamespaces", e);
          return resolve(result);
        });
    });
  }
}
